<template>
  <div :class="['signature-wrapper', `position-${controlPosition}`]">
    <div
      v-if="showControls"
      class="signature-controls"
      :style="{ justifyContent: controlJustify, alignItems: controlAlign }"
    >
      <b-button size="sm" variant="outline-secondary" @click="undo" :disabled="historyStep <= 0"
        >Hoàn tác</b-button
      >
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="redo"
        :disabled="historyStep >= history.length - 1"
        >Làm lại</b-button
      >
      <b-button size="sm" variant="outline-danger" @click="clear">Xoá</b-button>
    </div>
    <div class="canvas-container">
      <canvas
        ref="canvas"
        :style="{ width: width + 'px', height: height + 'px' }"
        @mousedown="start"
        @mousemove="move"
        @mouseenter="enter"
        @touchstart="start"
        @touchmove="move"
        @touchend="end"
      />
    </div>
  </div>
</template>

<script>
  export default {
    name: 'SignaturePad',

    props: {
      width: { type: Number, default: 400 },
      height: { type: Number, default: 400 },
      showControls: { type: Boolean, default: false },
      controlPosition: { type: String, default: 'top' }, // top, bottom, left, right
      controlJustify: { type: String, default: 'center' }, // center, flex-start, flex-end, space-between
      controlAlign: { type: String, default: 'center' },
    },

    data() {
      return {
        ctx: null,
        drawing: false,
        points: [],
        hasDraw: false,
        ratio: window.devicePixelRatio || 1,
        lastTime: 0,
        currentWidth: 4,
        history: [],
        historyStep: -1,
      }
    },

    watch: {
      hasDraw(val) {
        this.$emit('changed', val)
      },
    },

    mounted() {
      this.initCanvas()
      window.addEventListener('mouseup', this.end)
      this.saveState()
    },

    beforeDestroy() {
      window.removeEventListener('mouseup', this.end)
    },

    methods: {
      initCanvas() {
        const canvas = this.$refs.canvas

        canvas.width = this.width * this.ratio
        canvas.height = this.height * this.ratio

        this.ctx = canvas.getContext('2d')
        this.ctx.scale(this.ratio, this.ratio)

        this.ctx.lineCap = 'round'
        this.ctx.lineJoin = 'round'
        this.ctx.strokeStyle = '#000'
        this.ctx.fillStyle = '#000'
      },

      getPos(e) {
        const rect = this.$refs.canvas.getBoundingClientRect()

        if (e.touches) {
          return {
            x: e.touches[0].clientX - rect.left,
            y: e.touches[0].clientY - rect.top,
          }
        }

        return {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        }
      },

      start(e) {
        const pos = this.getPos(e)

        this.drawing = true
        this.points = [pos]
        this.lastTime = Date.now()
        this.currentWidth = 4

        this.ctx.beginPath()
        this.ctx.arc(pos.x, pos.y, this.currentWidth / 2, 0, Math.PI * 2)
        this.ctx.fill()
      },

      move(e) {
        if (!this.drawing) return

        e.preventDefault()

        const pos = this.getPos(e)
        this.points.push(pos)

        const now = Date.now()
        const dt = Math.max(1, now - this.lastTime)
        this.lastTime = now

        const speed =
          Math.sqrt(
            Math.pow(pos.x - this.points[this.points.length - 2].x, 2) +
              Math.pow(pos.y - this.points[this.points.length - 2].y, 2)
          ) / dt

        const targetWidth = Math.max(0.5, 4 - speed * 2.5)
        this.currentWidth += (targetWidth - this.currentWidth) * 0.2
        this.ctx.lineWidth = this.currentWidth

        if (this.points.length < 3) {
          const p1 = this.points[0]
          this.ctx.beginPath()
          this.ctx.moveTo(p1.x, p1.y)
          this.ctx.lineTo(pos.x, pos.y)
          this.ctx.stroke()
          return
        }

        const p1 = this.points[this.points.length - 3]
        const p2 = this.points[this.points.length - 2]
        const p3 = this.points[this.points.length - 1]

        const mid1 = {
          x: (p1.x + p2.x) / 2,
          y: (p1.y + p2.y) / 2,
        }

        const mid2 = {
          x: (p2.x + p3.x) / 2,
          y: (p2.y + p3.y) / 2,
        }

        this.ctx.beginPath()
        this.ctx.moveTo(mid1.x, mid1.y)
        this.ctx.quadraticCurveTo(p2.x, p2.y, mid2.x, mid2.y)
        this.ctx.stroke()

        this.hasDraw = true
      },

      enter(e) {
        if (e.buttons !== 1) {
          this.end()
          return
        }
        if (this.drawing) {
          const pos = this.getPos(e)
          this.points = [pos]
          this.lastTime = Date.now()
          this.ctx.beginPath()
          this.ctx.moveTo(pos.x, pos.y)
        }
      },

      end() {
        if (!this.drawing) return
        this.drawing = false
        this.points = []
        this.saveState()
      },

      saveState() {
        if (this.historyStep < this.history.length - 1) {
          this.history = this.history.slice(0, this.historyStep + 1)
        }
        this.history.push(this.$refs.canvas.toDataURL('image/png'))
        this.historyStep++
      },

      undo() {
        if (this.historyStep > 0) {
          this.historyStep--
          this.restoreState()
        }
      },

      redo() {
        if (this.historyStep < this.history.length - 1) {
          this.historyStep++
          this.restoreState()
        }
      },

      restoreState() {
        const img = new Image()
        img.onload = () => {
          this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height)
          this.ctx.drawImage(img, 0, 0, this.width, this.height)
          this.hasDraw = this.historyStep > 0
        }
        img.src = this.history[this.historyStep]
      },

      clear() {
        const canvas = this.$refs.canvas
        this.ctx.clearRect(0, 0, canvas.width, canvas.height)

        if (this.hasDraw || this.historyStep === -1) {
          this.hasDraw = false
          this.saveState()
        }
      },

      isEmpty() {
        return !this.hasDraw
      },

      getBase64() {
        return this.$refs.canvas.toDataURL('image/png')
      },

      loadBase64(base64) {
        const img = new Image()

        img.onload = () => {
          this.clear()
          this.ctx.drawImage(img, 0, 0, this.width, this.height)
          this.hasDraw = true
        }

        img.src = base64
      },

      cropCanvas() {
        const canvas = this.$refs.canvas
        const ctx = canvas.getContext('2d')
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height)

        let top = null
        let left = null
        let right = null
        let bottom = null

        for (let y = 0; y < img.height; y++) {
          for (let x = 0; x < img.width; x++) {
            const index = (y * img.width + x) * 4
            const alpha = img.data[index + 3]

            if (alpha > 0) {
              if (top === null) top = y
              if (left === null || x < left) left = x
              if (right === null || x > right) right = x
              bottom = y
            }
          }
        }

        if (top === null) return canvas

        const width = right - left
        const height = bottom - top

        const cropped = document.createElement('canvas')
        const croppedCtx = cropped.getContext('2d')

        cropped.width = width
        cropped.height = height

        croppedCtx.drawImage(canvas, left, top, width, height, 0, 0, width, height)

        return cropped
      },

      async getFile(isCrop = false) {
        const canvas = isCrop ? this.cropCanvas() : this.$refs.canvas

        return new Promise(resolve => {
          canvas.toBlob(blob => {
            const file = new File([blob], 'signature.png', {
              type: 'image/png',
            })
            resolve(file)
          })
        })
      },

      /**
       * Crop visible region (top-left) from the large canvas and return as File.
       * @param {number} visibleWidth  - CSS width of the visible container (px)
       * @param {number} visibleHeight - CSS height of the visible container (px)
       */
      async getFileNew(visibleWidth, visibleHeight) {
        const sourceCanvas = this.$refs.canvas
        const ratio = this.ratio

        const cropW = Math.min(visibleWidth * ratio, sourceCanvas.width)
        const cropH = Math.min(visibleHeight * ratio, sourceCanvas.height)

        const cropped = document.createElement('canvas')
        cropped.width = cropW
        cropped.height = cropH

        const ctx = cropped.getContext('2d')
        // Draw only the visible top-left region from the main canvas
        ctx.drawImage(sourceCanvas, 0, 0, cropW, cropH, 0, 0, cropW, cropH)

        return new Promise(resolve => {
          cropped.toBlob(blob => {
            const file = new File([blob], 'signature.png', { type: 'image/png' })
            resolve(file)
          })
        })
      },
    },
  }
</script>

<style scoped>
  .signature-wrapper {
    display: flex;
    gap: 8px;
  }
  .signature-wrapper.position-top {
    flex-direction: column;
  }
  .signature-wrapper.position-bottom {
    flex-direction: column-reverse;
  }
  .signature-wrapper.position-left {
    flex-direction: row;
  }
  .signature-wrapper.position-right {
    flex-direction: row-reverse;
  }

  .signature-controls {
    display: flex;
    gap: 8px;
  }

  canvas {
    /* border: 1px dotted #ccc;
    border-radius: 6px; */
    background: transparent;
    touch-action: none;
  }
</style>
